﻿import React, { useState, useEffect, useLayoutEffect, useRef, ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Input,
  Checkbox,
  Dropdown,
  Option,
  Combobox,
  makeStyles,
  shorthands,
  tokens,
  Spinner,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Badge,
  Tooltip,
} from '@fluentui/react-components';
import {
  Settings24Regular,
  Home24Regular,
  Tag24Regular,
  Dismiss24Regular,
  People24Regular,
} from '@fluentui/react-icons';
import type { TabConfig } from '../components/TabbedDialog';
import { queryKeys } from '../lib/queryClient';
import {
  updateCustomer,
  setCustomerCompany,
  deriveCustomerKind,
  fetchCustomerContacts,
  type Customer,
  type CustomerKind,
  type LinkedCompany,
} from '../services/customerService';
import CompanyPicker from '../components/CompanyPicker';
import CustomerNameWithHoverCard from '../components/CustomerNameWithHoverCard';
import {
  GeneralSection,
  AddressSection,
  ContactsSection,
  TagsSection,
} from '../components/customerForm';
import {
  COUNTRY_NAME_TO_ISO,
  ISO_TO_COUNTRY_NAME,
  splitName,
} from '../components/customerForm/helpers';
import {
  type CustomerFormBag,
  type CustomerFormSection,
  type CustomerFormSections,
  type CustomerFormVariant,
  type CustomerFormSaveStatus,
} from '../components/customerForm/types';
import { useDebug, debugLog } from '../contexts/DebugContext';
import { apiFetch } from '../lib/apiFetch';
import { BOOQABLE_API_URL } from '../config/booqable';
import { parsePhoneNumberFromString, AsYouType, type CountryCode } from 'libphonenumber-js';

// Re-export for callers that still import these names from the hook
// module (avoids breaking external consumers during the migration).
export {
  COUNTRY_NAME_TO_ISO,
  ISO_TO_COUNTRY_NAME,
  splitName,
};
export type {
  CustomerFormBag,
  CustomerFormSection,
  CustomerFormSections,
  CustomerFormVariant,
  CustomerFormSaveStatus,
};

// Normalize a user-typed phone number to E.164 ("+13065907534") for storage.
// Keeps the original input on parse failure so we don't silently drop data.
function toE164(rawPhone: string, countryName: string): string {
  const trimmed = rawPhone.trim();
  if (!trimmed) return trimmed;
  const parsed = parsePhoneNumberFromString(trimmed, COUNTRY_NAME_TO_ISO[countryName]);
  return parsed && parsed.isValid() ? parsed.format('E.164') : trimmed;
}

interface UseCustomerFormOptions {
  variant: CustomerFormVariant;
  /** Required when variant='edit' */
  customerId?: string | null;
  /** Pre-select customer kind on open. Only honored in 'add' mode. */
  initialKind?: CustomerKind;
  /** Pre-bind the parent company on a Contact-add flow (e.g. opened from
   *  the parent Company's profile). Skips having the user re-pick the
   *  company they're already viewing. Only honored in 'add' mode +
   *  initialKind='contact'. */
  initialLinkedCompanyId?: string | null;
  initialLinkedCompanyName?: string;
  isOpen: boolean;
  onClose: () => void;
  onSaved?: (customer: any) => void;
}


interface UseCustomerFormResult {
  /** Title node (string for edit, JSX for add with debug badge) */
  title: ReactNode;
  /** Form sections, keyed by conceptual chunk. Caller composes the
   *  layout â€” render `Object.values(sections)` for the drawer's stacked
   *  scroll, or pluck individual keys to compose custom layouts. */
  sections: CustomerFormSections;
  /** Bag of state + handlers passed to section components when surfaces
   *  want full layout control. */
  bag: CustomerFormBag;
  /** Banner area: errors and add-mode debug preview panel */
  headerContent: ReactNode;
  /** Footer buttons (cancel + save, plus debug-mode preview toggle in add) */
  actions: ReactNode;
  /** Customer name (useful for drawer subtitle) */
  customerName: string;
  /** True while a save (PATCH or chained POST) is in flight */
  saving: boolean;
  /**
   * Auto-save status (edit mode only). 'idle' before/after save, 'unsaved'
   * during the debounce window, 'saving' while a request is in flight,
   * 'saved' for ~2s after success, 'error' on failure.
   */
  saveStatus: CustomerFormSaveStatus;
  /**
   * Flush any pending auto-save synchronously. Call this from the chrome's
   * close handler so unsaved edits aren't lost when the drawer dismisses.
   */
  flushPendingSave: () => Promise<void>;
  /**
   * True when the form has unsaved drift from its baseline. In Add mode the
   * baseline is empty defaults (use this to confirm-on-close so the user
   * doesn't lose typed data). In Edit mode, true between a user edit and the
   * subsequent successful auto-save.
   */
  isDirty: boolean;
  /** Current entity kind for the form (drives chrome labels like the
   *  "Open full <kind> page" tooltip). */
  customerKind: CustomerKind;
  /** Switch the form's entity kind (Customer / Contact / Company). Wired to
   *  the in-drawer kind selector when `showKindSelector` is used. */
  handleCustomerKindChange: (newKind: CustomerKind) => void;
}

const useStyles = makeStyles({
  errorBanner: {
    ...shorthands.padding('12px', '24px'),
    backgroundColor: '#fef0f0',
    ...shorthands.borderBottom('1px', 'solid', '#fcd4d4'),
    color: '#c42b1c',
    fontSize: '14px',
    flexShrink: 0,
  },
  warningBanner: {
    ...shorthands.padding('12px', '24px'),
    backgroundColor: '#fff8e6',
    ...shorthands.borderBottom('1px', 'solid', '#f7e3a3'),
    color: '#7a5c00',
    fontSize: '14px',
    flexShrink: 0,
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 600,
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
    '& svg': {
      color: tokens.colorNeutralForeground2,
      flexShrink: 0,
    },
  },
  formField: {
    display: 'flex',
    flexDirection: 'column',
    // Matches the customerForm shared `formField`. The hook's own JSX
    // is gone (sections moved to component files) so this rule's only
    // a safety net for any stragglers — kept in sync to avoid drift.
    ...shorthands.gap('2px'),
    width: '100%',
    paddingBottom: 0,
  },
  formRow: {
    display: 'flex',
    ...shorthands.gap('12px'),
    width: '100%',
  },
  label: {
    fontSize: '14px',
    fontWeight: 600,
  },
  // Inline field-level error message â€” sits next to the field name in red.
  // Used for validation errors that previously surfaced via tooltips.
  fieldErrorText: {
    color: tokens.colorPaletteRedForeground1,
    fontWeight: 400,
    marginLeft: '8px',
  },
  customerAvatar: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    backgroundColor: '#0078d4',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    fontWeight: 600,
    flexShrink: 0,
  },
  tagField: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    ...shorthands.gap('6px'),
    minHeight: '40px',
    ...shorthands.padding('6px', '8px'),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    cursor: 'text',
    '&:focus-within': {
      ...shorthands.borderColor(tokens.colorBrandStroke1),
    },
  },
  tagChip: {
    display: 'inline-flex',
    alignItems: 'center',
    ...shorthands.gap('4px'),
    ...shorthands.padding('2px', '4px', '2px', '10px'),
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
    ...shorthands.borderRadius('14px'),
    fontSize: '13px',
    lineHeight: '20px',
  },
  tagDismiss: {
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '18px',
    height: '18px',
    ...shorthands.borderRadius('50%'),
    fontSize: '14px',
    lineHeight: '14px',
    '&:hover': {
      backgroundColor: tokens.colorBrandBackgroundHover,
      color: tokens.colorNeutralForegroundOnBrand,
    },
  },
  tagInlineInput: {
    flexGrow: 1,
    minWidth: '120px',
    ...shorthands.border('none'),
    ...shorthands.outline('none'),
    backgroundColor: 'transparent',
    color: tokens.colorNeutralForeground1,
    fontFamily: tokens.fontFamilyBase,
    fontSize: tokens.fontSizeBase300,
    ...shorthands.padding('4px', '6px'),
    '&::placeholder': {
      color: tokens.colorNeutralForeground3,
    },
  },
  emptyStateBlock: {
    color: tokens.colorNeutralForeground3,
    fontSize: '14px',
    ...shorthands.padding('16px', '0'),
  },
  previewContainer: {
    ...shorthands.padding('16px'),
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.borderRadius('4px'),
    margin: '0 24px 16px 24px',
    maxHeight: '500px',
    overflowY: 'auto',
  },
  previewHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '8px',
  },
  previewTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
  previewSubtitle: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    marginBottom: '12px',
  },
  previewCode: {
    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
    fontSize: '12px',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.padding('12px'),
    ...shorthands.borderRadius('4px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    overflowX: 'auto',
  },
  previewStep: {
    marginBottom: '16px',
    paddingBottom: '16px',
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    '&:last-child': {
      marginBottom: '0',
      paddingBottom: '0',
      borderBottom: 'none',
    },
  },
  previewStepTitle: {
    fontSize: '13px',
    fontWeight: 600,
    marginBottom: '8px',
    color: tokens.colorNeutralForeground1,
  },
});

const EMPTY_FORM = {
  customerName: '',
  email: '',
  subscribedToMarketing: false,
  customerKind: 'individual' as CustomerKind,
  linkedCompanyId: null as string | null,
  linkedCompanyName: '',
  linkedCompanies: [] as LinkedCompany[],
  phone: '',
  phoneExtension: '',
  allowedPaymentMethods: [] as string[],
  poRequired: false,
  // PR services-withholding rate (fraction, e.g. 0.10) auto-applied to this
  // customer's invoices; null = not a withholding agent.
  prWithholdingRate: null as number | null,
  title: '',
  notes: '',
  company: '',
  country: 'United States',
  addressLine1: '',
  addressLine2: '',
  city: '',
  region: '',
  postalCode: '',
  billingFirstName: '',
  billingLastName: '',
  billingNameOverride: false,
  tags: [] as string[],
  taxRegionId: '',
};

/**
 * useCustomerForm — owns all customer drawer state, side effects, and builds
 * the renderable bits (title, tabs, headerContent, actions). `CustomerDrawer`
 * consumes this hook for both Add and Edit variants so the form is defined
 * exactly once.
 *
 * Edit mode auto-saves on field change (debounced). Add mode keeps the
 * explicit Create button â€” no record exists to auto-save against, and we
 * don't want to leave half-populated draft customers if the user abandons.
 */
export function useCustomerForm({
  variant,
  customerId,
  initialKind,
  initialLinkedCompanyId,
  initialLinkedCompanyName,
  isOpen,
  onClose,
  onSaved,
}: UseCustomerFormOptions): UseCustomerFormResult {
  const styles = useStyles();
  const queryClient = useQueryClient();
  const { debugMode } = useDebug();
  const isEdit = variant === 'edit';

  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<CustomerFormSaveStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  // Field-level validation errors parsed out of JSON:API-style backend
  // messages like "/data/attributes/email: Validation Error". When a field
  // entry exists, the generic error banner is suppressed in favor of an
  // inline tooltip + red border on the offending field.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [showPreview, setShowPreview] = useState(false);
  const debounceTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedToastTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Form state â€” single name field for both individual and company modes,
  // matching Booqable's data model. The label changes per customerKind
  // (Customer name vs. Company name) but the underlying state is one string.
  const [customerName, setCustomerName] = useState(EMPTY_FORM.customerName);
  const [email, setEmail] = useState(EMPTY_FORM.email);
  const [subscribedToMarketing, setSubscribedToMarketing] = useState(EMPTY_FORM.subscribedToMarketing);
  const [customerKind, setCustomerKind] = useState<CustomerKind>(EMPTY_FORM.customerKind);
  const [linkedCompanyId, setLinkedCompanyId] = useState<string | null>(EMPTY_FORM.linkedCompanyId);
  const [linkedCompanyName, setLinkedCompanyName] = useState<string>(EMPTY_FORM.linkedCompanyName);
  const [linkedCompanies, setLinkedCompanies] = useState<LinkedCompany[]>(EMPTY_FORM.linkedCompanies);
  const [phone, setPhone] = useState(EMPTY_FORM.phone);
  const [phoneExtension, setPhoneExtension] = useState(EMPTY_FORM.phoneExtension);
  const [allowedPaymentMethods, setAllowedPaymentMethods] = useState<string[]>(EMPTY_FORM.allowedPaymentMethods);
  const [poRequired, setPoRequired] = useState<boolean>(EMPTY_FORM.poRequired);
  const [prWithholdingRate, setPrWithholdingRate] = useState<number | null>(EMPTY_FORM.prWithholdingRate);
  const [contactTitle, setContactTitle] = useState(EMPTY_FORM.title);
  const [notes, setNotes] = useState(EMPTY_FORM.notes);
  const [company, setCompany] = useState(EMPTY_FORM.company);
  const [country, setCountry] = useState(EMPTY_FORM.country);
  const [addressLine1, setAddressLine1] = useState(EMPTY_FORM.addressLine1);
  const [addressLine2, setAddressLine2] = useState(EMPTY_FORM.addressLine2);
  const [city, setCity] = useState(EMPTY_FORM.city);
  const [region, setRegion] = useState(EMPTY_FORM.region);
  const [postalCode, setPostalCode] = useState(EMPTY_FORM.postalCode);
  const [billingFirstName, setBillingFirstName] = useState(EMPTY_FORM.billingFirstName);
  const [billingLastName, setBillingLastName] = useState(EMPTY_FORM.billingLastName);
  const [billingNameOverride, setBillingNameOverride] = useState(EMPTY_FORM.billingNameOverride);
  const [transitionWarning, setTransitionWarning] = useState<string | null>(null);
  const transitionWarningTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const [tags, setTags] = useState<string[]>(EMPTY_FORM.tags);
  const [newTag, setNewTag] = useState('');
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [taxRegionId, setTaxRegionId] = useState<string>(EMPTY_FORM.taxRegionId);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Snapshot of the form values when an edit-mode customer is loaded. Used in
  // handleSave to diff against the current state so we only send changed
  // fields. Skipping unchanged phone/company/address keys lets the backend
  // skip the corresponding Booqable upserts (each ~2 round trips).
  const initialEditValuesRef = React.useRef<Partial<Customer> | null>(null);
  // Tracks which customerId the form has finished populating into state for.
  // Auto-save uses this as a guard so it doesn't run on the same render as
  // populate (where setStates haven't taken effect yet, which would otherwise
  // produce a spurious "name required" error and a phantom save).
  const [populatedFor, setPopulatedFor] = useState<string | null>(null);

  const resetForm = React.useCallback(() => {
    setCustomerName(EMPTY_FORM.customerName);
    setEmail(EMPTY_FORM.email);
    setSubscribedToMarketing(EMPTY_FORM.subscribedToMarketing);
    setCustomerKind(EMPTY_FORM.customerKind);
    setLinkedCompanyId(EMPTY_FORM.linkedCompanyId);
    setLinkedCompanyName(EMPTY_FORM.linkedCompanyName);
    setLinkedCompanies(EMPTY_FORM.linkedCompanies);
    setPhone(EMPTY_FORM.phone);
    setPhoneExtension(EMPTY_FORM.phoneExtension);
    setAllowedPaymentMethods(EMPTY_FORM.allowedPaymentMethods);
    setPoRequired(EMPTY_FORM.poRequired);
    setPrWithholdingRate(EMPTY_FORM.prWithholdingRate);
    setContactTitle(EMPTY_FORM.title);
    setNotes(EMPTY_FORM.notes);
    setCompany(EMPTY_FORM.company);
    setCountry(EMPTY_FORM.country);
    setAddressLine1(EMPTY_FORM.addressLine1);
    setAddressLine2(EMPTY_FORM.addressLine2);
    setCity(EMPTY_FORM.city);
    setRegion(EMPTY_FORM.region);
    setPostalCode(EMPTY_FORM.postalCode);
    setBillingFirstName(EMPTY_FORM.billingFirstName);
    setBillingLastName(EMPTY_FORM.billingLastName);
    setBillingNameOverride(EMPTY_FORM.billingNameOverride);
    setTransitionWarning(null);
    setTags(EMPTY_FORM.tags);
    setNewTag('');
    setAvatarUrl(null);
    setTaxRegionId(EMPTY_FORM.taxRegionId);
    setError(null);
    setFieldErrors({});
    setShowPreview(false);
  }, []);

  // Map a JSON:API attribute pointer to a human-friendly field message.
  // Returns null when the message doesn't match a recognized pointer, so
  // the caller can fall back to a generic banner.
  const parseFieldError = React.useCallback((message: string): { field: string; message: string } | null => {
    const match = message.match(/\/data\/attributes\/(\w+)\s*:\s*([^.]+)/i);
    if (!match) return null;
    const field = match[1].toLowerCase();
    const friendly: Record<string, string> = {
      email: 'Please enter a valid email address.',
      name: 'Please enter a valid name.',
    };
    return { field, message: friendly[field] || match[2].trim() };
  }, []);

  useEffect(() => {
    return () => {
      if (avatarUrl) URL.revokeObjectURL(avatarUrl);
    };
  }, [avatarUrl]);

  // Reset the Add form on OPEN, not on close. Fluent's overlay drawer stays
  // mounted while it slides out (~250ms); resetting on close made the body
  // visibly snap from the Contact/Company layout back to the Individual one
  // mid-animation. Resetting on the closed→open transition keeps the content
  // stable through the slide-out, and useLayoutEffect applies it before paint
  // so the reopen doesn't flash the previous session's state either. The
  // pre-selected kind (from the Add SplitButton) is applied in the same pass.
  // Edit mode re-populates from cache on reopen, so it's excluded here.
  const wasOpenRef = useRef(false);
  useLayoutEffect(() => {
    if (variant !== 'add') return;
    if (isOpen && !wasOpenRef.current) {
      resetForm();
      if (initialKind) setCustomerKind(initialKind);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, variant, initialKind, resetForm]);

  // Pre-bind the parent company on Add-Contact flows opened from the parent
  // Company's profile. Skips having the user re-pick a company they're
  // already viewing.
  useEffect(() => {
    if (isOpen && variant === 'add' && initialKind === 'contact' && initialLinkedCompanyId) {
      setLinkedCompanyId(initialLinkedCompanyId);
      setLinkedCompanyName(initialLinkedCompanyName || '');
    }
  }, [isOpen, variant, initialKind, initialLinkedCompanyId, initialLinkedCompanyName]);

  const { data: taxRegionsRaw, isLoading: taxRegionsLoading } = useQuery({
    queryKey: ['tax-regions'],
    queryFn: async () => {
      const response = await apiFetch(`/tax-regions`);
      if (!response.ok) throw new Error(`Failed to fetch tax regions: ${response.statusText}`);
      const result = await response.json();
      return result.data || [];
    },
    // Only fetch when the form is actually open. The add CustomerDrawer is kept
    // mounted on the Customers page so Drawer animations
    // work â€” without this gate the query would fire on page load and a stale 401
    // would poison the cache for the full staleTime window.
    enabled: isOpen,
    staleTime: 10 * 60 * 1000,
  });

  // Customer-side rows only — the merged /tax-regions response includes
  // product tax profiles too, which don't apply to a customer's tax setting.
  const taxRegionsData = React.useMemo(
    () => (taxRegionsRaw || []).filter((tr: any) => tr.attributes?.kind !== 'product'),
    [taxRegionsRaw],
  );

  // For the Add drawer, pre-select a sensible default tax profile so staff
  // don't have to manually pick the common case. Preference order:
  //   1. The "IVU" profile by name (Puerto Rico sales tax — the overwhelming
  //      common case for TWM customers)
  //   2. Whichever profile Booqable flags as `default`
  // Runs once per drawer open after the regions list arrives; skipped for
  // Edit (the customer's saved tax_region_id wins). The dropdown options
  // key on booqable_id, so we set the booqable_id here — not the local row id.
  const autoAppliedDefaultRef = React.useRef(false);
  useEffect(() => {
    if (!isOpen) {
      autoAppliedDefaultRef.current = false;
      return;
    }
    if (variant !== 'add') return;
    if (autoAppliedDefaultRef.current) return;
    if (!taxRegionsData || taxRegionsData.length === 0) return;
    const ivuRegion = taxRegionsData.find(
      (tr: any) => (tr.attributes?.name || '').trim().toUpperCase() === 'IVU',
    );
    const booqableDefault = taxRegionsData.find((tr: any) => {
      const a = tr.attributes || {};
      return a.default === true || a.is_default === true;
    });
    const chosen = ivuRegion || booqableDefault;
    if (chosen) {
      setTaxRegionId(chosen.attributes?.booqable_id || '');
      autoAppliedDefaultRef.current = true;
    }
  }, [isOpen, variant, taxRegionsData]);

  const { data: customerResponse, error: customerError } = useQuery({
    queryKey: queryKeys.customers.detail(customerId || ''),
    queryFn: async () => {
      if (!customerId) throw new Error('No customer ID');
      const response = await apiFetch(`/booqable/customers/${customerId}`);
      if (!response.ok) throw new Error(`Failed to fetch customer: ${response.statusText}`);
      return response.json();
    },
    enabled: isEdit && !!customerId && isOpen,
    // No initialData here. The list query (queryKeys.customers.all) returns
    // flat Customer objects from mapDbCustomer; the detail key consumers
    // (this hook + CustomerProfileComponent) expect the JSON:API envelope
    // shape ({ id, type, attributes, relationships, included }). Seeding
    // the detail cache from the list would leave `customerData.attributes`
    // undefined for the profile page, so the header sticks at "Loading...".
    staleTime: 10 * 60 * 1000,
  });

  const customerData = customerResponse?.data;
  const includedData = customerResponse?.included;

  // Linked contacts for the Contacts tab. Only fetched when editing a Company
  // â€” Individuals/Contacts have no contact list of their own. Reuses the same
  // query key the conflict-delete modal uses so a recent fetch from one
  // doesn't double-load for the other.
  const { data: linkedContacts = [], isLoading: linkedContactsLoading } = useQuery({
    queryKey: ['company-contacts', customerId || ''],
    queryFn: () => fetchCustomerContacts(customerId!),
    enabled: isEdit && isOpen && !!customerId && customerKind === 'company',
    staleTime: 60 * 1000,
  });

  useEffect(() => {
    if (!isEdit) {
      initialEditValuesRef.current = null;
      setPopulatedFor(null);
      return;
    }
    if (!isOpen) {
      setTags([]);
      setNewTag('');
      initialEditValuesRef.current = null;
      setPopulatedFor(null);
      // Drop any lingering validation state so reopening (same or different
      // customer) starts clean â€” no stale tooltip / red outline.
      setFieldErrors({});
      setError(null);
      setSaveStatus('idle');
      return;
    }
    if (!customerData) return;
    // Once we've populated this customerId, the form state is owned by the
    // user. Skip subsequent re-populates triggered by cache invalidations
    // after a successful save â€” otherwise unsaved edits to other fields
    // (e.g. an in-progress invalid email) get clobbered by the server's
    // last-known value. The snapshot is kept in sync separately via the
    // mutation's onSuccess.
    if (populatedFor === customerId) return;

    const attrs = customerData.attributes || customerData;
    const included = includedData || [];

    const properties = included.filter((item: any) => item.type === 'properties');
    const phoneProperty = properties.find((p: any) => p.attributes?.identifier === 'phone');
    const companyProperty = properties.find((p: any) => p.attributes?.identifier === 'company');

    // Compute parsed values into locals first so we can both setState AND
    // snapshot them to initialEditValuesRef in a single consistent pass.
    const parsedName = attrs.name || customerData.name || '';
    const parsedEmail = attrs.email || customerData.email || '';
    const parsedPhone = phoneProperty?.attributes?.value || '';
    // phone_extension is a TWM column stamped onto attributes by the backend
    // (_attach_company_link), not a Booqable property like phone.
    const parsedPhoneExtension = attrs.phone_extension || '';
    // allowed_payment_methods is a TWM column stamped onto attributes by the
    // backend (_attach_company_link). null/absent → [] (card-only default).
    const parsedAllowedMethods: string[] = Array.isArray(attrs.allowed_payment_methods)
      ? attrs.allowed_payment_methods
      : [];
    // po_required is a TWM-owned boolean column stamped onto attributes.
    // (email_opt_in / is_billing_contact are per-membership — they ride on
    // linked_companies, parsed into the LinkedCompany set below.)
    const parsedPoRequired = !!attrs.po_required;
    // pr_withholding_rate is a TWM-owned numeric column (fraction) stamped onto
    // attributes; null/absent = not a withholding agent.
    const parsedPrWithholdingRate = typeof attrs.pr_withholding_rate === 'number'
      ? attrs.pr_withholding_rate
      : null;
    // title (contact-only) and notes are TWM-owned columns stamped onto
    // attributes by the backend (_attach_company_link), not Booqable fields.
    const parsedTitle = attrs.title || '';
    const parsedNotes = attrs.notes || '';
    const parsedCompany = companyProperty?.attributes?.value || '';

    // Booqable v4 stores addresses as a property with identifier='main_address'
    // and property_type='address' â€” NOT as a separate `addresses` JSON:API
    // resource. Structured fields (address1/address2/city/region/country/
    // zipcode/first_name/last_name) live on that property's attributes.
    const mainAddressProperty = properties.find(
      (p: any) => p.attributes?.identifier === 'main_address'
    );

    let parsedAddress1 = '';
    let parsedAddress2 = '';
    let parsedCity = '';
    let parsedRegion = '';
    let parsedPostal = '';
    let parsedCountry = 'United States';
    let parsedBillingFirst = '';
    let parsedBillingLast = '';
    if (mainAddressProperty?.attributes) {
      const a = mainAddressProperty.attributes;
      parsedAddress1 = a.address1 || '';
      parsedAddress2 = a.address2 || '';
      parsedCity = a.city || '';
      parsedRegion = a.region || '';
      parsedPostal = a.zipcode || '';
      parsedCountry = a.country || 'United States';
      parsedBillingFirst = a.first_name || '';
      parsedBillingLast = a.last_name || '';
    }

    const parsedMarketing = attrs.email_marketing_consented || false;
    // Three-entity model: derive customerKind from legal_type + company_id.
    // Backend's _attach_company_link surfaces company_id on attributes; if it
    // isn't present (older/unmirrored data) we fall back to legal_type alone.
    // Prefer raw Booqable values; only fall back to mapped Customer fields
    // (customerType: 'Company' | 'Individual' / customerKind) when raw is
    // absent â€” the prior `legal_type || customerType` could promote a stale
    // 'Company' string to a commercial classification when legal_type was
    // missing/empty on the raw payload.
    const parsedLinkedCompanyId: string | null =
      attrs.company_id || customerData.linkedCompanyId || null;
    const parsedLinkedCompanyName: string =
      attrs.linked_company_name || customerData.linkedCompanyName || '';
    const parsedCustomerKind: CustomerKind = attrs.legal_type
      ? deriveCustomerKind(attrs.legal_type, parsedLinkedCompanyId)
      : (customerData.customerKind as CustomerKind | undefined)
        || deriveCustomerKind(customerData.customerType, parsedLinkedCompanyId);
    if (typeof window !== 'undefined') {
      console.log('[useCustomerForm] kind derivation', {
        customerId,
        rawLegalType: attrs.legal_type,
        mappedCustomerType: customerData.customerType,
        mappedCustomerKind: customerData.customerKind,
        linkedCompanyId: parsedLinkedCompanyId,
        result: parsedCustomerKind,
      });
    }

    let parsedTags: string[] = [];
    const tagList = attrs.tag_list || customerData.tags;
    if (tagList) {
      if (typeof tagList === 'string') {
        parsedTags = tagList.split(',').map((t: string) => t.trim()).filter(Boolean);
      } else if (Array.isArray(tagList)) {
        parsedTags = tagList.map((t: string) => t.trim()).filter(Boolean);
      }
    }

    const parsedTaxRegion = attrs.tax_region_id || customerData.tax_region_id || '';

    // Initial billing-name override state. For individual customers, the
    // checkbox is checked when the stored billing name doesn't match
    // splitName(customer.name) AND a billing name was actually set. For
    // company, override is irrelevant (fields always shown) â€” we still
    // initialize to false. A coincidental match between billing and
    // customer name reads as "auto-derived" â€” behavior is identical, so
    // there's no harm.
    const customerSplit = splitName(parsedName);
    const billingMatchesCustomer =
      parsedBillingFirst === customerSplit.first &&
      parsedBillingLast === customerSplit.last;
    const noBillingSet = !parsedBillingFirst && !parsedBillingLast;
    const parsedBillingOverride =
      parsedCustomerKind === 'individual'
        ? !billingMatchesCustomer && !noBillingSet
        : false;

    setCustomerName(parsedName);
    setEmail(parsedEmail);
    // Reformat the persisted phone (Booqable stores E.164 like
    // "+15555551234") into the national display so the field reads as
    // "(555) 555-1234" the moment the drawer opens â€” without waiting for
    // a blur event to trigger the same reformatting. We capture the
    // formatted value into a local so the snapshot below can use the same
    // representation (computeDiff would otherwise read the field as
    // "unsaved" the moment the drawer renders).
    let displayPhone = parsedPhone || '';
    if (displayPhone) {
      const parsedNum = parsePhoneNumberFromString(
        displayPhone,
        COUNTRY_NAME_TO_ISO[parsedCountry] as CountryCode | undefined,
      );
      if (parsedNum && parsedNum.isValid()) displayPhone = parsedNum.formatNational();
    }
    setPhone(displayPhone);
    setPhoneExtension(parsedPhoneExtension);
    setAllowedPaymentMethods(parsedAllowedMethods);
    setPoRequired(parsedPoRequired);
    setPrWithholdingRate(parsedPrWithholdingRate);
    setContactTitle(parsedTitle);
    setNotes(parsedNotes);
    setCompany(parsedCompany);
    setAddressLine1(parsedAddress1);
    setAddressLine2(parsedAddress2);
    setCity(parsedCity);
    setRegion(parsedRegion);
    setPostalCode(parsedPostal);
    setCountry(parsedCountry);
    setSubscribedToMarketing(parsedMarketing);
    setCustomerKind(parsedCustomerKind);
    setLinkedCompanyId(parsedLinkedCompanyId);
    setLinkedCompanyName(parsedLinkedCompanyName);
    // Backend surfaces the membership set at attributes.linked_companies
    // (snake_case, with is_primary), NOT customerData.linkedCompanies — reading
    // the wrong path left the edit-mode "Companies" editor empty so a contact's
    // company never showed on its profile. Map to the camelCase LinkedCompany.
    setLinkedCompanies(
      Array.isArray(attrs.linked_companies)
        ? attrs.linked_companies.map((c: any) => ({
            id: c.id,
            name: c.name || '',
            isPrimary: !!c.is_primary,
            isBillingContact: !!c.is_billing_contact,
            isMainContact: !!c.main_contact,
            emailOptIn: !!c.email_opt_in,
          }))
        : []
    );
    setTags(parsedTags);
    setTaxRegionId(parsedTaxRegion);
    // Companies don't carry a person's name on the billing record â€” the
    // company name itself is the billing identity. Force '' so the form
    // state matches that intent regardless of any stale value Booqable
    // returned (e.g., from a prior Individual classification).
    const initialBillingFirst = parsedCustomerKind === 'company' ? '' : parsedBillingFirst;
    const initialBillingLast = parsedCustomerKind === 'company' ? '' : parsedBillingLast;
    setBillingFirstName(initialBillingFirst);
    setBillingLastName(initialBillingLast);
    setBillingNameOverride(parsedBillingOverride);

    // Effective billing name as it would be sent right now â€” used for both
    // the snapshot and the diff. For individual + no override, derive from
    // customer name; otherwise use the stored billing fields. Companies
    // get empty strings (no person name).
    const effectiveFirst =
      parsedCustomerKind === 'company'
        ? ''
        : parsedCustomerKind === 'individual' && !parsedBillingOverride
          ? customerSplit.first
          : parsedBillingFirst;
    const effectiveLast =
      parsedCustomerKind === 'company'
        ? ''
        : parsedCustomerKind === 'individual' && !parsedBillingOverride
          ? customerSplit.last
          : parsedBillingLast;

    // Snapshot the parsed values â€” handleSave diffs current state against
    // these to send only changed fields. Use the same `effectiveCompany`
    // logic as computeDiff so a Company whose Booqable `company` property
    // is empty/out-of-sync with `name` doesn't read as dirty on open.
    const effectiveCompany =
      parsedCustomerKind === 'company' ? parsedName : parsedCompany;
    initialEditValuesRef.current = {
      name: parsedName,
      email: parsedEmail,
      customerType: parsedCustomerKind === 'company' ? 'Company' : 'Individual',
      customerKind: parsedCustomerKind,
      linkedCompanyId: parsedLinkedCompanyId,
      tags: parsedTags,
      // Snapshot the same formatted display value we just put in state â€”
      // otherwise computeDiff sees "+15555551234" (snapshot) vs
      // "(555) 555-1234" (state) and flags the field as drift on open,
      // triggering a phantom auto-save on close.
      phone: displayPhone,
      phoneExtension: parsedPhoneExtension,
      allowedPaymentMethods: parsedAllowedMethods,
      poRequired: parsedPoRequired,
      prWithholdingRate: parsedPrWithholdingRate,
      title: parsedTitle,
      notes: parsedNotes,
      company: effectiveCompany,
      address1: parsedAddress1,
      address2: parsedAddress2,
      city: parsedCity,
      region: parsedRegion,
      zipcode: parsedPostal,
      country: parsedCountry,
      address_first_name: effectiveFirst,
      address_last_name: effectiveLast,
      tax_region_id: parsedTaxRegion || null,
    };
    // Mark population complete for this customer. Auto-save will only run
    // once this matches customerId, which guarantees state has had a render
    // to catch up to the values we just snapshotted.
    if (customerId) setPopulatedFor(customerId);

  }, [isEdit, customerData, includedData, isOpen]);

  const getInitials = (name: string) => {
    if (!name) return '??';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
  };

  // Handles transitions across the three customer kinds. The customer's
  // name field is preserved across all transitions; only ancillary state
  // (override, linked company) gets adjusted.
  //
  // Transitions:
  //   â”€â†’ Contact  : clear override; clear billing fields (Contact's billing
  //                 rolls up to Company; we don't show or send those).
  //   Contact â†’ â”€ : clear linkedCompanyId so the unlink saves.
  //   Company â†’ Individual / Individual â†’ Company: existing billing-override
  //                 transition behavior (warn, seed) carries over.
  const handleCustomerKindChange = (newKind: CustomerKind) => {
    if (newKind === customerKind) return;

    // Going Contact â†’ anything else: drop the link so save sends null.
    if (customerKind === 'contact' && newKind !== 'contact') {
      setLinkedCompanyId(null);
      setLinkedCompanyName('');
      setLinkedCompanies([]);
    }

    // Going anything â†’ Contact: clear billing override + fields (billing
    // for Contacts is inherited from the parent Company).
    if (newKind === 'contact') {
      setBillingNameOverride(false);
      setBillingFirstName('');
      setBillingLastName('');
    }

    if (newKind === 'individual' && customerKind === 'company') {
      // Company â†’ Individual: warn if billing name diverged.
      const customerSplit = splitName(customerName);
      const customWasSet =
        (billingFirstName || billingLastName) &&
        (billingFirstName !== customerSplit.first || billingLastName !== customerSplit.last);
      if (customWasSet) {
        const wasName = `${billingFirstName} ${billingLastName}`.trim();
        setTransitionWarning(
          `Billing name will reset to match customer name (was: "${wasName}")`
        );
        if (transitionWarningTimerRef.current) clearTimeout(transitionWarningTimerRef.current);
        transitionWarningTimerRef.current = setTimeout(() => setTransitionWarning(null), 6000);
      }
      setBillingNameOverride(false);
    } else if (newKind === 'company' && customerKind === 'individual') {
      // Individual â†’ Company: seed billing fields from the customer name
      // if empty so the user has a starting point to edit.
      if (!billingFirstName && !billingLastName) {
        const split = splitName(customerName);
        setBillingFirstName(split.first);
        setBillingLastName(split.last);
      }
    }
    setCustomerKind(newKind);
  };

  const handleAddTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(t => t !== tagToRemove));
  };

  const handleUploadClick = () => fileInputRef.current?.click();

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }
    setAvatarUrl(URL.createObjectURL(file));
    // TODO: Upload to server/cloud storage
  };

  const updateMutation = useMutation({
    mutationFn: (data: Partial<Customer>) => {
      if (!customerId) throw new Error('No customer ID');
      return updateCustomer(customerId, data);
    },
    onSuccess: (updated, variables) => {
      // Optimistic: patch every list-query variant (booqable / local) using
      // the values we just sent (`variables`). Avoid spreading `updated`
      // because the PATCH response only carries customer attributes â€” not
      // properties (phone/company) â€” so spreading it would clobber existing
      // list-cache values with undefined.
      queryClient.setQueriesData<Customer[]>(
        { queryKey: queryKeys.customers.all },
        (old) => {
          // Detail entries also match the ['customers'] prefix; filter to
          // arrays only so we don't try to .map an envelope object.
          if (!Array.isArray(old)) return old;
          return old.map((c) => (c.id === customerId ? { ...c, ...variables } : c));
        }
      );

      // Surgical patch of the detail cache so the next drawer open shows fresh
      // data without a refetch. The form values (in `variables`) are the
      // source of truth at save time â€” we mirror them into the JSON:API
      // envelope shape the drawer's populate effect expects.
      if (customerId) {
        const detailKey = queryKeys.customers.detail(customerId);
        const existing = queryClient.getQueryData<{ data: any; included: any[] }>(detailKey);
        if (existing && existing.data) {
          const v = variables;
          const patchedData = {
            ...existing.data,
            attributes: {
              ...(existing.data.attributes || {}),
              ...(v.name !== undefined ? { name: v.name } : {}),
              ...(v.email !== undefined ? { email: v.email } : {}),
              ...(v.customerType !== undefined
                ? { legal_type: v.customerType === 'Company' ? 'commercial' : 'person' }
                : {}),
              ...(v.customerKind !== undefined
                ? { legal_type: v.customerKind === 'company' ? 'commercial' : 'person' }
                : {}),
              ...(v.linkedCompanyId !== undefined ? { company_id: v.linkedCompanyId } : {}),
              ...(v.tags !== undefined ? { tag_list: v.tags } : {}),
              ...(v.phoneExtension !== undefined ? { phone_extension: v.phoneExtension || '' } : {}),
              ...(v.allowedPaymentMethods !== undefined ? { allowed_payment_methods: v.allowedPaymentMethods || [] } : {}),
              ...(v.poRequired !== undefined ? { po_required: v.poRequired } : {}),
              ...(v.prWithholdingRate !== undefined ? { pr_withholding_rate: v.prWithholdingRate } : {}),
              ...(v.title !== undefined ? { title: v.title || '' } : {}),
              ...(v.notes !== undefined ? { notes: v.notes || '' } : {}),
              ...(v.tax_region_id !== undefined ? { tax_region_id: v.tax_region_id } : {}),
            },
          };

          // Patch property entries (phone, company) â€” update existing or append new.
          const patchProperty = (
            included: any[],
            identifier: 'phone' | 'company',
            value: string | undefined,
          ): any[] => {
            if (value === undefined) return included;
            const idx = included.findIndex(
              (i) => i.type === 'properties' && i.attributes?.identifier === identifier
            );
            if (idx >= 0) {
              const next = [...included];
              next[idx] = {
                ...next[idx],
                attributes: { ...next[idx].attributes, value },
              };
              return next;
            }
            if (!value) return included; // empty string and no existing entry â†’ noop
            return [
              ...included,
              {
                type: 'properties',
                id: `optimistic-${identifier}-${customerId}`,
                attributes: {
                  identifier,
                  value,
                  owner_id: customerId,
                  owner_type: 'customers',
                },
              },
            ];
          };

          let patchedIncluded = existing.included || [];
          patchedIncluded = patchProperty(patchedIncluded, 'phone', v.phone);
          patchedIncluded = patchProperty(patchedIncluded, 'company', v.company);

          // Patch the main_address property entry. Booqable stores address
          // as a property (identifier='main_address', property_type='address'),
          // not a separate `addresses` resource.
          const addressFieldsTouched =
            v.address1 !== undefined ||
            v.address2 !== undefined ||
            v.city !== undefined ||
            v.region !== undefined ||
            v.zipcode !== undefined ||
            v.country !== undefined ||
            v.address_first_name !== undefined ||
            v.address_last_name !== undefined;

          if (addressFieldsTouched) {
            const idx = patchedIncluded.findIndex(
              (i) => i.type === 'properties' && i.attributes?.identifier === 'main_address'
            );
            const addressAttrs = {
              ...(idx >= 0
                ? patchedIncluded[idx].attributes
                : {
                    identifier: 'main_address',
                    property_type: 'address',
                    owner_id: customerId,
                    owner_type: 'customers',
                  }),
              ...(v.address1 !== undefined ? { address1: v.address1 } : {}),
              ...(v.address2 !== undefined ? { address2: v.address2 } : {}),
              ...(v.city !== undefined ? { city: v.city } : {}),
              ...(v.region !== undefined ? { region: v.region } : {}),
              ...(v.zipcode !== undefined ? { zipcode: v.zipcode } : {}),
              ...(v.country !== undefined ? { country: v.country } : {}),
              ...(v.address_first_name !== undefined ? { first_name: v.address_first_name } : {}),
              ...(v.address_last_name !== undefined ? { last_name: v.address_last_name } : {}),
            };
            if (idx >= 0) {
              patchedIncluded = [
                ...patchedIncluded.slice(0, idx),
                { ...patchedIncluded[idx], attributes: addressAttrs },
                ...patchedIncluded.slice(idx + 1),
              ];
            } else {
              patchedIncluded = [
                ...patchedIncluded,
                {
                  type: 'properties',
                  id: `optimistic-main_address-${customerId}`,
                  attributes: addressAttrs,
                },
              ];
            }
          }

          queryClient.setQueryData(detailKey, {
            data: patchedData,
            included: patchedIncluded,
          });
        }
      }

      // Update the snapshot so subsequent diffs measure against the new
      // baseline. Without this, the next auto-save would re-send the same
      // fields we just persisted.
      if (initialEditValuesRef.current) {
        initialEditValuesRef.current = { ...initialEditValuesRef.current, ...variables };
      }

      // Backstop the optimistic patch with an invalidation â€” Customers list
      // sometimes fails to reflect tag removals via the cache patch alone
      // (cause unidentified; symptom is the chip lingers until manual
      // refresh). Safe for attribute PATCH because Booqable's read replicas
      // settle quickly here, unlike the archive replication that delete
      // contends with.
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });

      // Briefly flash "Saved" then return to idle. Auto-save does NOT close
      // the drawer â€” closing is the user's job (or the chrome's flush path
      // when they dismiss).
      setSaveStatus('saved');
      if (savedToastTimerRef.current) clearTimeout(savedToastTimerRef.current);
      savedToastTimerRef.current = setTimeout(() => {
        setSaveStatus((s) => (s === 'saved' ? 'idle' : s));
      }, 2000);

      onSaved?.(updated);
    },
    onError: (err) => {
      const message = err instanceof Error ? err.message : 'Failed to update customer';
      const parsed = parseFieldError(message);
      if (parsed) {
        // Field-level error â€” surface via tooltip + outline only. Don't paint
        // the global "Save failed" badge for what reads as a per-field issue.
        setFieldErrors((prev) => ({ ...prev, [parsed.field]: parsed.message }));
        setError(null);
        setSaveStatus((s) => (s === 'saving' ? 'idle' : s));
      } else {
        setError(message);
        setSaveStatus('error');
      }
      // Invalidate so the list and detail caches refetch fresh state â€” drops
      // any optimistic patches we applied before the failure surfaced (e.g.
      // an address that the backend logged as a warning).
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
    },
  });

  // Diff current form state against the snapshot taken when the customer
  // loaded. Returns only the fields that have changed â€” feeds the auto-save
  // mutation so we never send no-op payloads.
  const computeDiff = React.useCallback((): Partial<Customer> => {
    // Effective billing name:
    //  - Individual + no-override â†’ derive from customer name
    //  - Individual + override    â†’ use explicit billing fields
    //  - Company                  â†’ no person name on the billing record;
    //                                 send empty strings so Booqable clears
    //                                 any prior first/last (often stale from
    //                                 an earlier Individual classification).
    const billingDerive = customerKind === 'individual' && !billingNameOverride;
    const customerSplit = splitName(customerName);
    const effectiveFirst = customerKind === 'company'
      ? ''
      : billingDerive ? customerSplit.first : billingFirstName;
    const effectiveLast = customerKind === 'company'
      ? ''
      : billingDerive ? customerSplit.last : billingLastName;

    // Company mode: the Booqable `company` property mirrors customer.name.
    // Individual / Contact: leave the property untouched (no UI field writes
    // to it now that "company affiliation" is gone â€” the structured Contact
    // â†” Company link is the proper relationship).
    const effectiveCompany = customerKind === 'company' ? customerName : company;

    // For Contacts, billing/tax/address fields aren't editable in the UI
    // (they roll up to the parent Company). Don't put them in the diff so
    // we don't clobber whatever's on the underlying Booqable record from
    // its Individual/Company past â€” that's latent data, not user intent.
    const isContact = customerKind === 'contact';

    // "Real address" = any actual address line present. country is excluded:
    // it auto-defaults to United States, so it alone doesn't make an address.
    // The form is seeded from the saved main_address on load, so this also
    // reflects whether the customer has a saved main address.
    const hasRealAddress = !!(
      addressLine1 || addressLine2 || city || region || postalCode
    );

    // Only emit the derived billing first/last when there's a real address to
    // attach them to, the billing name is explicitly overridden, or we're
    // clearing stale name on a Company. Otherwise a name-only edit on an
    // address-less individual would diff the auto-derived billing name and
    // trigger a Booqable main_address write carrying ONLY first/last name (no
    // address lines) â€” which Booqable rejects with 422.
    const includeBillingName =
      customerKind === 'company' || billingNameOverride || hasRealAddress;

    const current: Partial<Customer> = {
      name: customerName,
      email,
      customerType: customerKind === 'company' ? 'Company' : 'Individual',
      customerKind,
      linkedCompanyId,
      tags,
      phone,
      phoneExtension,
      allowedPaymentMethods,
      // notes is a TWM-owned free-text column that applies to all customer
      // kinds (no contact gate) — distinct from the order-scoped Customer Notes.
      notes,
      // title is contact-only (job title / role).
      ...(isContact ? { title: contactTitle } : {}),
      ...(isContact ? {} : {
        // poRequired is a billed-entity flag (company / individual only).
        poRequired,
        // prWithholdingRate — PR services-withholding, billed entities only.
        prWithholdingRate,
        company: effectiveCompany,
        address1: addressLine1,
        address2: addressLine2,
        city,
        region,
        zipcode: postalCode,
        country,
        ...(includeBillingName ? {
          address_first_name: effectiveFirst,
          address_last_name: effectiveLast,
        } : {}),
        tax_region_id: taxRegionId || null,
      }),
    };
    const initial = initialEditValuesRef.current;
    if (!initial) return current;

    const isEqual = (a: any, b: any) => {
      if (Array.isArray(a) && Array.isArray(b)) {
        return a.length === b.length && a.every((v, i) => v === b[i]);
      }
      return a === b;
    };

    const diff: Partial<Customer> = {};
    (Object.keys(current) as (keyof Customer)[]).forEach((k) => {
      if (!isEqual(current[k], initial[k])) {
        (diff as any)[k] = current[k];
      }
    });
    return diff;
  }, [
    customerName, email, customerKind, linkedCompanyId, tags, phone, phoneExtension,
    allowedPaymentMethods, poRequired, prWithholdingRate, contactTitle, notes, company,
    addressLine1, addressLine2, city, region, postalCode, country, taxRegionId,
    billingFirstName, billingLastName, billingNameOverride,
  ]);

  // "Unsaved" indicator (edit mode only). Watches every editable field and
  // sets saveStatus to 'unsaved' when there's drift from the snapshot.
  // Does NOT trigger a save â€” that's the caller's job (via onBlur on each
  // field, or via flushPendingSave on close).
  useEffect(() => {
    if (!isEdit || !isOpen) return;
    if (!initialEditValuesRef.current) return;
    if (populatedFor !== customerId) return;

    // Field-level validation errors take precedence â€” they own their own
    // tooltip + outline UX, the global save badge would just add noise.
    if (Object.keys(fieldErrors).length > 0) {
      setSaveStatus('idle');
      return;
    }

    const diff = computeDiff();
    if (Object.keys(diff).length === 0) {
      // No drift â€” clear stale error/unsaved state but keep "saved" toast
      // and an in-flight save status alone.
      setSaveStatus((s) => (s === 'saved' || s === 'saving' ? s : 'idle'));
      setError(null);
      return;
    }
    setSaveStatus((s) => (s === 'saving' ? s : 'unsaved'));
  }, [isEdit, isOpen, computeDiff, populatedFor, customerId, fieldErrors]);

  // Trigger an immediate save with the current diff. Wire to onBlur on every
  // field. Idempotent: if there's nothing to save, returns silently.
  const triggerSave = React.useCallback(async () => {
    if (!isEdit) return;
    if (populatedFor !== customerId) return;
    if (!initialEditValuesRef.current) return;
    if (updateMutation.isPending) return;

    const diff = computeDiff();
    if (Object.keys(diff).length === 0) return;

    if (!customerName.trim()) {
      setSaveStatus('error');
      setError('Customer name is required');
      return;
    }
    if (customerKind === 'contact' && !linkedCompanyId) {
      setSaveStatus('error');
      setError('A Contact must be linked to a Company');
      return;
    }

    // Per-field validation. A bad email/phone shouldn't block other unrelated
    // changes from saving â€” instead, mark that field invalid (inline error)
    // and drop it from the diff. Other fields go through normally.
    const trimmedEmail = email.trim();
    const emailInvalid = trimmedEmail.length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);
    if (emailInvalid) {
      setFieldErrors((prev) => ({ ...prev, email: 'Please enter a valid email address.' }));
      delete (diff as any).email;
    }
    const trimmedPhone = phone.trim();
    if (trimmedPhone) {
      const parsedPhone = parsePhoneNumberFromString(trimmedPhone, COUNTRY_NAME_TO_ISO[country]);
      if (!parsedPhone || !parsedPhone.isValid()) {
        setFieldErrors((prev) => ({ ...prev, phone: 'Please enter a valid phone number.' }));
        delete (diff as any).phone;
      }
    }
    // Postal code: optional, but if provided must match the country's shape.
    // Empty is allowed and saves through; only a partial/wrong-format value
    // surfaces an inline error.
    const trimmedZip = postalCode.trim();
    const postalValid =
      trimmedZip.length === 0
        ? true
        : country === 'United States'
          ? /^\d{5}$/.test(trimmedZip)
          : country === 'Canada'
            ? /^[A-Z]\d[A-Z] \d[A-Z]\d$/.test(trimmedZip)
            : true;
    if (!postalValid && (diff as any).zipcode !== undefined) {
      const msg =
        country === 'United States'
          ? 'ZIP must be 5 digits.'
          : country === 'Canada'
            ? 'Postal code must be in A1A 1A1 format.'
            : 'Postal code format is invalid.';
      setFieldErrors((prev) => ({ ...prev, postalCode: msg }));
      delete (diff as any).zipcode;
    }

    // After dropping invalid fields, nothing else to save â†’ just stay idle.
    if (Object.keys(diff).length === 0) {
      setSaveStatus((s) => (s === 'saving' ? s : 'idle'));
      return;
    }

    // Normalize phone to E.164 right before sending â€” display state stays
    // formatted, but the persisted value is canonical so search / dedupe /
    // SMS APIs all work with the same string.
    if ((diff as any).phone) {
      (diff as any).phone = toE164((diff as any).phone, country);
    }

    // Companies don't carry person-name fields on their billing address.
    // Whenever a save fires for one, force address_first_name/last_name to
    // empty strings so Booqable clears any stale values left over from a
    // prior Individual classification. Doesn't trigger a save on its own
    // (we only land here once the diff is non-empty for some other reason).
    if (customerKind === 'company') {
      (diff as any).address_first_name = '';
      (diff as any).address_last_name = '';
    }

    setError(null);
    setSaveStatus('saving');
    try {
      await updateMutation.mutateAsync(diff);
    } catch {
      // Status set to 'error' by the mutation's onError handler.
    }
  }, [isEdit, populatedFor, customerId, computeDiff, customerName, email, phone, postalCode, country, customerKind, linkedCompanyId, updateMutation]);

  // Auto-save tag changes. Tag add/remove updates state without a blur
  // event, so we'd otherwise rely on the user blurring the inline input
  // (which doesn't happen on Backspace-remove). Snapshot per-customer so
  // reopening another customer doesn't fire a phantom save against the
  // previous one's tags.
  const lastSavedTagsRef = React.useRef<{ id: string | null; tags: string[] }>({ id: null, tags: [] });
  useEffect(() => {
    if (!isEdit || !customerId || populatedFor !== customerId) return;
    if (lastSavedTagsRef.current.id !== customerId) {
      lastSavedTagsRef.current = { id: customerId, tags };
      return;
    }
    const prev = lastSavedTagsRef.current.tags;
    const same = prev.length === tags.length && prev.every((v, i) => v === tags[i]);
    if (same) return;
    lastSavedTagsRef.current = { id: customerId, tags };
    triggerSave();
  }, [tags, isEdit, populatedFor, customerId, triggerSave]);

  // Auto-save Contact↔Company link changes. CompanyPicker selection updates
  // state without a blur, so calling triggerSave from its onChange would
  // capture the prior linkedCompanyId via the useCallback closure and bail
  // out of the diff with "nothing to save". Watch state instead — by the
  // time this effect runs the state update has flushed and triggerSave's
  // closure sees the new value. Snapshot per-customer to avoid a phantom
  // save on initial population.
  const lastSavedLinkRef = React.useRef<{ id: string | null; linkedCompanyId: string | null }>({ id: null, linkedCompanyId: null });
  useEffect(() => {
    if (!isEdit || !customerId || populatedFor !== customerId) return;
    if (lastSavedLinkRef.current.id !== customerId) {
      lastSavedLinkRef.current = { id: customerId, linkedCompanyId };
      return;
    }
    if (lastSavedLinkRef.current.linkedCompanyId === linkedCompanyId) return;
    lastSavedLinkRef.current = { id: customerId, linkedCompanyId };
    triggerSave();
  }, [linkedCompanyId, isEdit, populatedFor, customerId, triggerSave]);

  // Auto-save allowed-payment-method changes. Checkbox toggles update state
  // without a blur, so (like tags) watch state and fire triggerSave once the
  // update has flushed. Snapshot per-customer to avoid a phantom save on the
  // initial population.
  const lastSavedMethodsRef = React.useRef<{ id: string | null; methods: string[] }>({ id: null, methods: [] });
  useEffect(() => {
    if (!isEdit || !customerId || populatedFor !== customerId) return;
    if (lastSavedMethodsRef.current.id !== customerId) {
      lastSavedMethodsRef.current = { id: customerId, methods: allowedPaymentMethods };
      return;
    }
    const prev = lastSavedMethodsRef.current.methods;
    const same = prev.length === allowedPaymentMethods.length
      && prev.every((v, i) => v === allowedPaymentMethods[i]);
    if (same) return;
    lastSavedMethodsRef.current = { id: customerId, methods: allowedPaymentMethods };
    triggerSave();
  }, [allowedPaymentMethods, isEdit, populatedFor, customerId, triggerSave]);

  // Auto-save the poRequired boolean flag. Checkbox toggles don't blur, so
  // (like allowedPaymentMethods) watch state and fire triggerSave once the
  // update flushes. Snapshot per-customer to skip the phantom save on initial
  // population. (email_opt_in / is_billing_contact are per-membership — they
  // save via LinkedCompaniesEditor's own PATCH, not this diff.)
  const lastSavedPoRequiredRef = React.useRef<{ id: string | null; value: boolean }>({ id: null, value: false });
  useEffect(() => {
    if (!isEdit || !customerId || populatedFor !== customerId) return;
    if (lastSavedPoRequiredRef.current.id !== customerId) {
      lastSavedPoRequiredRef.current = { id: customerId, value: poRequired };
      return;
    }
    if (lastSavedPoRequiredRef.current.value === poRequired) return;
    lastSavedPoRequiredRef.current = { id: customerId, value: poRequired };
    triggerSave();
  }, [poRequired, isEdit, populatedFor, customerId, triggerSave]);

  // Auto-save the PR withholding rate the same way — toggled via a checkbox
  // (no blur), so watch state and fire triggerSave once the update flushes.
  const lastSavedPrWithholdingRef = React.useRef<{ id: string | null; value: number | null }>({ id: null, value: null });
  useEffect(() => {
    if (!isEdit || !customerId || populatedFor !== customerId) return;
    if (lastSavedPrWithholdingRef.current.id !== customerId) {
      lastSavedPrWithholdingRef.current = { id: customerId, value: prWithholdingRate };
      return;
    }
    if (lastSavedPrWithholdingRef.current.value === prWithholdingRate) return;
    lastSavedPrWithholdingRef.current = { id: customerId, value: prWithholdingRate };
    triggerSave();
  }, [prWithholdingRate, isEdit, populatedFor, customerId, triggerSave]);

  // Chrome calls this from its close handler so a close-during-typing still
  // persists the edit. With on-blur saves, the only "pending" state is text
  // the user typed without yet blurring out of the field.
  const flushPendingSave = triggerSave;

  // Cleanup pending timers on unmount.
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      if (savedToastTimerRef.current) clearTimeout(savedToastTimerRef.current);
      if (transitionWarningTimerRef.current) clearTimeout(transitionWarningTimerRef.current);
    };
  }, []);

  const buildAddCustomerRequest = () => {
    const attributes: Record<string, any> = {
      name: customerName,
      email_marketing_consented: subscribedToMarketing,
      legal_type: customerKind === 'company' ? 'commercial' : 'person',
    };
    if (email) attributes.email = email;
    // Persist a region picked in the Add drawer (GH #613). The edit/autosave
    // payload already sends tax_region_id (see buildUpdatePayload); the create
    // payload omitted it, so an explicit selection was dropped on save and the
    // customer ended up region-less — which then feeds untaxed orders (#608/#611).
    if (taxRegionId) attributes.tax_region_id = taxRegionId;
    // Booqable expects tag_list as an array of strings (JSON:API spec).
    // Sending a joined string returns: data.attributes.tag_list should be type array_of_strings.
    if (tags.length > 0) attributes.tag_list = tags;
    // Tax region picked in the drawer must ride on the create payload — omitting
    // it here silently dropped the selection (#613). Booqable accepts
    // tax_region_id on customer create; the dropdown stores the Booqable id.
    if (taxRegionId) attributes.tax_region_id = taxRegionId;

    // Fold phone / company / address into this single create call via Booqable's
    // properties_attributes, rather than firing a separate POST /properties for
    // each afterward. Same identifiers/fields as the old standalone calls — just
    // nested, so the customer and its properties are created in one round trip.
    // Booqable rejects a properties_attributes entry whose `name` is blank
    // ("Properties[N] name can't be blank", 422). Each entry must carry a
    // human label — mirror the standalone upsert paths (upsert_customer_property
    // sends name=label, upsert_customer_main_address sends name='Main address').
    const properties: Record<string, any>[] = [];
    if (phone) {
      properties.push({ identifier: 'phone', name: 'Phone', value: toE164(phone, country) });
    }
    const companyValue = customerKind === 'company' ? customerName : company;
    if (companyValue) {
      properties.push({ identifier: 'company_name', name: 'Company', value: companyValue });
    }
    // Billing name mirrors edit mode: an individual without the override derives
    // first/last from the customer name; otherwise use the explicit billing fields.
    const billingDerive = customerKind === 'individual' && !billingNameOverride;
    const customerSplit = splitName(customerName);
    const effectiveBillingFirst = billingDerive ? customerSplit.first : billingFirstName;
    const effectiveBillingLast = billingDerive ? customerSplit.last : billingLastName;
    const hasAddress =
      addressLine1 || addressLine2 || city || region || postalCode ||
      effectiveBillingFirst || effectiveBillingLast;
    if (hasAddress) {
      const addr: Record<string, string> = {
        identifier: 'main_address',
        property_type: 'address',
        name: 'Main address',
      };
      if (addressLine1) addr.address1 = addressLine1;
      if (addressLine2) addr.address2 = addressLine2;
      if (city) addr.city = city;
      if (region) addr.region = region;
      if (postalCode) addr.zipcode = postalCode;
      // Always send country (GH #284 — Booqable silently defaults to US otherwise).
      if (country) addr.country = country;
      if (effectiveBillingFirst) addr.first_name = effectiveBillingFirst;
      if (effectiveBillingLast) addr.last_name = effectiveBillingLast;
      properties.push(addr);
    }
    if (properties.length > 0) attributes.properties_attributes = properties;

    // A contact is created as legal_type='person' (identical to a regular
    // individual) and associated to its company in a separate call below, so
    // the backend can't tell them apart at create time. Flag contacts so the
    // backend skips Auth.Net account creation — only regular customers and
    // companies get one. Backend pops `is_contact` before forwarding to Booqable.
    const request: Record<string, any> = { data: { type: 'customers', attributes } };
    if (customerKind === 'contact') request.is_contact = true;
    return request;
  };

  const handleSave = async () => {
    // Edit mode is auto-save â€” handleSave is only the Add-mode chained POST.
    if (isEdit) return;

    // Belt-and-suspenders: even though the Save button is disabled when an
    // individual/contact lacks an email (GH #291), guard the handler too in
    // case it's ever invoked programmatically (e.g. Enter-key submission).
    if (customerKind !== 'company' && !email.trim()) {
      setFieldErrors((prev) => ({ ...prev, email: 'Email is required.' }));
      return;
    }

    setError(null);
    setSaving(true);

    try {
      const customerPayload = buildAddCustomerRequest();
      debugLog('Step 1: Creating customer with data:', customerPayload);

      const customerResp = await apiFetch(`/booqable/create/customers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(customerPayload),
      });
      const customerResult = await customerResp.json();
      if (!customerResp.ok) {
        throw new Error(customerResult.error || `Failed to create customer: ${customerResp.statusText}`);
      }
      debugLog('Customer created successfully:', customerResult);
      const newCustomerId = customerResult.data.id;

      // phone, company_name, and the main address are created inline with the
      // customer via properties_attributes (see buildAddCustomerRequest) — one
      // round trip instead of a separate POST /properties for each.

      // phone_extension is a TWM column, not a Booqable property. The create
      // call above already mirrored a local customers row (keyed by
      // booqable_id), so a PATCH resolves it and writes the column.
      if (phoneExtension.trim()) {
        const extResp = await apiFetch(`/booqable/customers/${newCustomerId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            data: { type: 'customers', id: newCustomerId, attributes: {} },
            phone_extension: phoneExtension.trim(),
          }),
        });
        if (!extResp.ok) console.warn('Failed to save phone extension:', await extResp.json());
      }

      // allowed_payment_methods is likewise a TWM column — persist via a PATCH
      // after create when staff picked a non-default set (anything other than
      // the empty card-only default).
      if (allowedPaymentMethods.length > 0) {
        const apmResp = await apiFetch(`/booqable/customers/${newCustomerId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            data: { type: 'customers', id: newCustomerId, attributes: {} },
            allowed_payment_methods: allowedPaymentMethods,
          }),
        });
        if (!apmResp.ok) console.warn('Failed to save allowed payment methods:', await apmResp.json());
      }

      // poRequired is a TWM-owned boolean flag — persisted via a PATCH after
      // create, but only when non-default (defaults false). Applies to billed
      // entities (company / individual). (email_opt_in / is_billing_contact are
      // per-membership and aren't set at create time — they're edited per
      // company in the LinkedCompaniesEditor once the contact exists.)
      if (customerKind !== 'contact' && poRequired) {
        const flagResp = await apiFetch(`/booqable/customers/${newCustomerId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            data: { type: 'customers', id: newCustomerId, attributes: {} },
            po_required: true,
          }),
        });
        if (!flagResp.ok) console.warn('Failed to save customer flags:', await flagResp.json());
      }

      // prWithholdingRate is a TWM column — persist via a PATCH after create,
      // only when set (non-null). Billed entities only.
      if (customerKind !== 'contact' && prWithholdingRate != null) {
        const whResp = await apiFetch(`/booqable/customers/${newCustomerId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            data: { type: 'customers', id: newCustomerId, attributes: {} },
            pr_withholding_rate: prWithholdingRate,
          }),
        });
        if (!whResp.ok) console.warn('Failed to save PR withholding rate:', await whResp.json());
      }

      // title is a TWM column, contact-only — persist via a PATCH after create.
      if (customerKind === 'contact' && contactTitle.trim()) {
        const titleResp = await apiFetch(`/booqable/customers/${newCustomerId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            data: { type: 'customers', id: newCustomerId, attributes: {} },
            title: contactTitle.trim(),
          }),
        });
        if (!titleResp.ok) console.warn('Failed to save title:', await titleResp.json());
      }

      // notes is a TWM column that applies to all customer kinds — persist via
      // a PATCH after create when staff entered anything.
      if (notes.trim()) {
        const notesResp = await apiFetch(`/booqable/customers/${newCustomerId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            data: { type: 'customers', id: newCustomerId, attributes: {} },
            notes: notes.trim(),
          }),
        });
        if (!notesResp.ok) console.warn('Failed to save notes:', await notesResp.json());
      }

      // Link Contact → Company. Backend resolves either ID type, so passing
      // the just-created Booqable id works.
      if (customerKind === 'contact' && linkedCompanyId) {
        try {
          await setCustomerCompany(newCustomerId, linkedCompanyId);
          // The link stamps company_id server-side AFTER the create response was
          // cached, so the contact's detail/list entries are now stale (10-min
          // staleTime) and the profile would show no company on first open.
          // Invalidate the whole customers family (detail keys share the
          // ['customers'] prefix) so the next read refetches with the company.
          queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
        } catch (linkErr) {
          console.warn('Failed to link contact to company:', linkErr);
        }
      }

      // Stamp the kind onto the saved record so the parent's onSaved toast
      // can label it correctly. customerResult.data is the raw Booqable
      // response (legal_type only) â€” the parent doesn't know whether this
      // was a Contact create vs. Individual create otherwise.
      onSaved?.({ ...customerResult.data, customerKind });
      onClose();
    } catch (err) {
      console.error('Error saving customer:', err);
      setError(err instanceof Error ? err.message : 'Failed to save customer');
    } finally {
      setSaving(false);
    }
  };

  // Bundle every piece of state + handler that section components need into
  // a single bag passed via the `form` prop. Constructed before the section
  // JSX is built so each tab's content can be a simple
  // `<XxxSection form={bag} />` instance.
  const bag: CustomerFormBag = {
    customerName, setCustomerName,
    email, setEmail,
    phone, setPhone,
    phoneExtension, setPhoneExtension,
    allowedPaymentMethods, setAllowedPaymentMethods,
    poRequired, setPoRequired,
    prWithholdingRate, setPrWithholdingRate,
    title: contactTitle, setTitle: setContactTitle,
    notes, setNotes,
    customerKind, setCustomerKind,
    linkedCompanyId, setLinkedCompanyId,
    linkedCompanyName, setLinkedCompanyName,
    linkedCompanies, setLinkedCompanies,
    subscribedToMarketing, setSubscribedToMarketing,
    country, setCountry,
    region, setRegion,
    city, setCity,
    postalCode, setPostalCode,
    addressLine1, setAddressLine1,
    addressLine2, setAddressLine2,
    billingFirstName, setBillingFirstName,
    billingLastName, setBillingLastName,
    billingNameOverride, setBillingNameOverride,
    tags, setTags,
    newTag, setNewTag,
    handleAddTag, handleRemoveTag,
    taxRegionId, setTaxRegionId,
    taxRegionsData: taxRegionsData || [],
    taxRegionsLoading,
    linkedContacts, linkedContactsLoading,
    customerId: customerId ?? null,
    fieldErrors, setFieldErrors,
    saveStatus, setSaveStatus,
    triggerSave,
    variant,
    isEdit,
  };

  // Section components own their own field JSX — see
  // `frontend/src/components/customerForm/`. Each consumes the `form`
  // bag (constructed above) so all hook state + handlers flow in via a
  // single prop. The tabs array shape is preserved for the legacy
  // `sections` Record<key, ReactNode> consumers; new callers can
  // bypass it and render the section components directly.
  const tabs: TabConfig[] = [
    {
      key: 'general',
      label: 'General',
      icon: <Settings24Regular />,
      content: <GeneralSection form={bag} />,
    },
    ...(customerKind !== 'contact' ? [{
      key: 'address',
      label: 'Address',
      icon: <Home24Regular />,
      content: <AddressSection form={bag} />,
    } as TabConfig] : []),
    ...(isEdit && customerKind === 'company' ? [{
      key: 'contacts',
      label: 'Contacts',
      icon: <People24Regular />,
      content: <ContactsSection form={bag} />,
    } as TabConfig] : []),
    ...(isEdit && customerKind !== 'contact' ? [{
      key: 'tags',
      label: 'Tags',
      icon: <Tag24Regular />,
      content: <TagsSection form={bag} />,
    } as TabConfig] : []),
  ];

  const headerContent = (error || customerError || transitionWarning || (showPreview && debugMode && !isEdit)) ? (
    <>
      {error && <div className={styles.errorBanner}>Error: {error}</div>}
      {customerError && (
        <div className={styles.errorBanner}>
          Error loading customer: {customerError instanceof Error ? customerError.message : String(customerError)}
        </div>
      )}
      {transitionWarning && (
        <div className={styles.warningBanner}>{transitionWarning}</div>
      )}
      {showPreview && debugMode && !isEdit && (
        <div className={styles.previewContainer}>
          <div className={styles.previewHeader}>
            <div className={styles.previewTitle}>API Request Chain Preview</div>
            <Button
              appearance="subtle"
              icon={<Dismiss24Regular />}
              onClick={() => setShowPreview(false)}
              title="Close preview"
            />
          </div>
          <div className={styles.previewStep}>
            <div className={styles.previewStepTitle}>Step 1: Create Customer</div>
            <div className={styles.previewSubtitle}>POST {BOOQABLE_API_URL}/customers</div>
            <div className={styles.previewCode}>
              {JSON.stringify(buildAddCustomerRequest(), null, 2)}
            </div>
          </div>
          {(phone || (customerKind === 'company' ? customerName : company) || addressLine1 || addressLine2 || city || region || postalCode) && (
            <div className={styles.previewStep}>
              <div className={styles.previewStepTitle}>Phone, company &amp; address ride along in Step 1</div>
              <div className={styles.previewSubtitle}>
                folded into the customer create via properties_attributes — no separate POST /properties calls
              </div>
            </div>
          )}
          {phoneExtension.trim() && (
            <div className={styles.previewStep}>
              <div className={styles.previewStepTitle}>Step 2: Save phone extension (TWM column)</div>
              <div className={styles.previewSubtitle}>PATCH {BOOQABLE_API_URL}/customers/&lt;customer_id&gt;</div>
            </div>
          )}
        </div>
      )}
    </>
  ) : null;

  const entityLabel =
    customerKind === 'company'
      ? 'Company'
      : customerKind === 'contact'
        ? 'Contact'
        : 'Customer';

  const title = isEdit ? (
    entityLabel
  ) : (
    <>
      {`Add New ${entityLabel}`}
      {debugMode && <span style={{ color: '#ff6b35', marginLeft: '8px', fontSize: '12px' }}>ðŸ› DEBUG MODE</span>}
    </>
  );

  // Edit mode autosaves on field change â€” only Close button is needed.
  // Add mode keeps the explicit "Create Customer" submit (plus Preview in
  // debug mode) since there's no record yet to autosave against.
  const actions = isEdit ? (
    <Button appearance="secondary" onClick={onClose}>
      Close
    </Button>
  ) : (
    <>
      <Button appearance="secondary" onClick={onClose} disabled={saving}>
        Cancel
      </Button>
      {debugMode && (
        <Button
          appearance="secondary"
          onClick={() => setShowPreview(!showPreview)}
          disabled={!customerName.trim() || saving}
        >
          {showPreview ? 'Hide Preview' : 'Preview'}
        </Button>
      )}
      <Button
        appearance="primary"
        onClick={handleSave}
        disabled={
          !customerName.trim() ||
          saving ||
          (customerKind === 'contact' && !linkedCompanyId) ||
          // Email is required when creating an individual or contact (GH #291).
          // Companies are exempt — they dedupe by name and frequently lack a
          // single billing email at create time.
          (customerKind !== 'company' && !email.trim())
        }
        icon={saving ? <Spinner size="tiny" /> : undefined}
      >
        {saving ? 'Saving...' : 'Save'}
      </Button>
    </>
  );

  // For Edit, dirty = the computed diff has any keys. For Add, dirty = any
  // field differs from EMPTY_FORM defaults (no snapshot in Add mode).
  const isDirty = isEdit
    ? Object.keys(computeDiff()).length > 0
    : (
      customerName !== EMPTY_FORM.customerName ||
      email !== EMPTY_FORM.email ||
      subscribedToMarketing !== EMPTY_FORM.subscribedToMarketing ||
      // customerKind is excluded â€” the Add SplitButton presets it to
      // 'contact' / 'company' before the user has typed anything, so a
      // pristine Add Contact / Add Company drawer would otherwise look
      // dirty and trigger the discard-confirm warning on cancel.
      linkedCompanyId !== EMPTY_FORM.linkedCompanyId ||
      phone !== EMPTY_FORM.phone ||
      contactTitle !== EMPTY_FORM.title ||
      notes !== EMPTY_FORM.notes ||
      company !== EMPTY_FORM.company ||
      country !== EMPTY_FORM.country ||
      addressLine1 !== EMPTY_FORM.addressLine1 ||
      addressLine2 !== EMPTY_FORM.addressLine2 ||
      city !== EMPTY_FORM.city ||
      region !== EMPTY_FORM.region ||
      postalCode !== EMPTY_FORM.postalCode ||
      billingFirstName !== EMPTY_FORM.billingFirstName ||
      billingLastName !== EMPTY_FORM.billingLastName ||
      billingNameOverride !== EMPTY_FORM.billingNameOverride ||
      tags.length !== 0 ||
      // taxRegionId is excluded from the Add-mode dirty check on purpose:
      // it gets auto-populated with the Booqable-designated default when the
      // drawer opens, so a pristine form would otherwise look dirty and trigger
      // the discard-confirm warning on cancel.
      avatarUrl !== null
    );

  // Derive the section map from the legacy `tabs` array build above so
  // we don't have to rewrite each tab's JSX. Tabs that don't apply to
  // the current entity kind / mode are simply absent from the array,
  // so the resulting map naturally omits them.
  const sections: CustomerFormSections = {};
  for (const tab of tabs) {
    sections[tab.key as CustomerFormSection] = tab.content;
  }

  return {
    title,
    sections,
    bag,
    headerContent,
    actions,
    customerName,
    saving,
    saveStatus,
    flushPendingSave,
    isDirty,
    customerKind,
    handleCustomerKindChange,
  };
}
